import request from 'utils/request'
import { browserHistory } from 'utils/navigation'
import { isPasswordRecoveryPath, isRememberableLastPath } from 'router/locationUtils'
import { clearAllRouteLoaderCache } from 'router/legacy/routeLoaderCache'
import { setUserLinktFlag } from 'utils/userUtils'
import { hasValidSession } from 'utils/sessionUtils'
import { logSentryEvent } from 'utils/sentry'

let authSessionGeneration = 0

// ------------------------------------
// Constants
// ------------------------------------

export const USER_LOGGED_OUT = 'USER_LOGGED_OUT'
export const UPDATE_USERNAME = 'UPDATE_USERNAME'
export const SET_USER_IMAGE = 'SET_USER_IMAGE'
export const SET_USER = 'SET_USER'
export const SET_LOGIN_ERROR = 'SET_LOGIN_ERROR'

const setLoginError = error => {
  return {
    type: SET_LOGIN_ERROR,
    error,
  }
}

export function fetchInsightsSetup() {
  return dispatch => {
    return dispatch(
      request({
        url: `/control/insights/setup`,
        success: textResponse => {
          return textResponse
        },
        hasLoader: true,
        loadingKey: 'insightsSetup',
        resType: 'text',
      }),
    ).catch(e => {
      logSentryEvent({
        message: 'Insights setup failed',
        level: 'error',
        tags: {
          module: 'store.user',
          action: 'fetchInsightsSetup',
        },
        error: e,
      })
      throw new Error(e)
    })
  }
}

export const fetchUser = (
  userData = {},
  hasLoader = true,
  redirectToResetPW,
  authRequired = true,
  logoutOnUnauthorized,
) => {
  return dispatch => {
    const requestGeneration = authSessionGeneration
    const localUserData = JSON.parse(localStorage.getItem('user'))
    const userDataWithAuth = userData?.token ? userData : localUserData
    const success = jsonBody => {
      if (requestGeneration !== authSessionGeneration) return Promise.resolve()
      const newJsonBody = jsonBody?.access_token
        ? Object.assign(JSON.parse(atob(jsonBody?.access_token.split('.')[1])), jsonBody)
        : jsonBody
      const payload = { ...userDataWithAuth, ...newJsonBody }
      // Never let a partial /me response wipe existing study permissions (causes /unauthorized).
      if (!payload.permissions || !Object.keys(payload.permissions).length) {
        if (userDataWithAuth?.permissions && Object.keys(userDataWithAuth.permissions).length) {
          payload.permissions = userDataWithAuth.permissions
        }
      }
      localStorage.setItem('user', JSON.stringify(payload))
      dispatch({
        type: SET_USER,
        payload,
      })
      if (redirectToResetPW) browserHistory.push('/users/reset')
      return Promise.resolve()
    }
    return dispatch(
      request({
        method: 'GET',
        url: '/control/users/me',
        credentials: true,
        hasLoader,
        success,
        userData: userDataWithAuth,
        authRequired,
        manualToken: null,
        logoutOnUnauthorized,
      }),
    )
  }
}

function login(username, password) {
  return dispatch => {
    // Manually start loader
    dispatch(require('store/loader').loadingActions.startLoader(true));
    const success = jsonBody => {
      const { expiration_message } = jsonBody;
      const payload = Object.assign(JSON.parse(atob(jsonBody.token.split('.')[1])), jsonBody);
      dispatch(setLoginError(''));
      localStorage.setItem('user', JSON.stringify(payload));

      if (expiration_message) {
        dispatch({ type: SET_USER, payload });
        browserHistory.push('/users/reset');
        if (__DEV__) console.log('[Auth] Redirecting to /users/reset (password expiration)');
        dispatch(fetchUser(payload, false, false));
        dispatch(fetchInsightsSetup()).catch(() => {
          logSentryEvent({
            message: 'Insights setup failed during login expiration flow',
            level: 'warning',
            tags: {
              module: 'store.user',
              action: 'login',
            },
          })
          if (__DEV__) console.log('Insights setup failed, continuing with login');
        });
        dispatch(require('store/loader').loadingActions.stopLoader(true));
        return;
      }

      // Wait for /me so redirect uses same-user check with API user id
      dispatch(fetchUser(payload, false, false))
        .then(() => {
          dispatch(require('store/loader').loadingActions.stopLoader(true));
          dispatch(fetchInsightsSetup()).catch(() => {
            logSentryEvent({
              message: 'Insights setup failed during login flow',
              level: 'warning',
              tags: {
                module: 'store.user',
                action: 'login',
              },
            })
            if (__DEV__) console.log('Insights setup failed, continuing with login');
          });
        })
        .catch(() => {
          logSentryEvent({
            message: '/control/users/me failed after login token decode',
            level: 'error',
            tags: {
              module: 'store.user',
              action: 'fetchUserAfterLogin',
            },
          })
          // /me failed; set user from token so HOC redirects to /studies
          dispatch({ type: SET_USER, payload });
          dispatch(require('store/loader').loadingActions.stopLoader(true));
        });
    };
    const fail = (res, content) => {
      logSentryEvent({
        message: 'Admin login request failed',
        level: 'error',
        tags: {
          module: 'store.user',
          action: 'login',
          'http.status': res?.status,
        },
        contexts: {
          auth: {
            username,
          },
        },
      })
      dispatch(setLoginError(content.message));
      // Manually stop loader on error
      dispatch(require('store/loader').loadingActions.stopLoader(true));
    };
    return dispatch(
      request({
        method: 'POST',
        url: '/control/admin/login',
        body: JSON.stringify({ username, password }),
        catchMessage: 'There was a problem logging in, please try again later.',
        catchFail: () => dispatch(require('store/loader').loadingActions.stopLoader(true)),
        authRequired: false,
        hasLoader: false, // disable auto loader
        success,
        fail,
      }),
    );
  }
}

export const LAST_PATH_KEY = 'lastPath'

/**
 * Returns the path to redirect to after login if the same user is logging back in; otherwise null.
 * Caller should fall back to /studies (or query.redirect) when this returns null.
 */
export function getPostLoginRedirectPath(user) {
  if (!user?.id) return null
  const lastUser = localStorage.getItem('lastUser')
  if (lastUser == null || String(lastUser) !== String(user.id)) return null
  const path = localStorage.getItem(LAST_PATH_KEY)
  if (!isRememberableLastPath(path)) return null
  return path
}

function logout(user = {}) {
  return (dispatch, getState) => {
    const stateUser = getState()?.user
    const userId = stateUser?.id ?? (JSON.parse(localStorage.getItem('user')) || user)?.id
    const currentPath = typeof window !== 'undefined' ? window.location?.pathname : null
    if (userId && isRememberableLastPath(currentPath)) {
      localStorage.setItem(LAST_PATH_KEY, currentPath)
    }
    if (userId) {
      localStorage.setItem('lastUser', userId)
      setUserLinktFlag(userId)
    }
    authSessionGeneration += 1
    localStorage.removeItem('user')
    // Study getChildRoutes fetches study data; cached children would skip that after re-login.
    clearAllRouteLoaderCache()
    dispatch({ type: USER_LOGGED_OUT })
    const path = typeof window !== 'undefined' ? window.location?.pathname : null
    if (path && path !== '/login' && !isPasswordRecoveryPath(path)) {
      browserHistory.replace('/login')
    }
    return Promise.resolve()
  }
}

function updateUsername(username) {
  return {
    type: UPDATE_USERNAME,
    payload: username,
  }
}

export function fetchUserImage(userID) {
  return dispatch => {
    const errorMessage = 'Could not fetch user image'
    return dispatch(
      request({
        method: 'GET',
        url: `/control/assets/profile_pics/${userID}`,
        headerOptions: { 'Content-Type': 'image' },
        errorMessage,
        success: json => {
          dispatch({
            type: SET_USER_IMAGE,
            payload: json.asset,
          })
        },
        fail: () => {
          if (__DEV__) console.log('user does not have an image')
        },
      }),
    )
  }
}

function getInitialUserState() {
  try {
    const user = JSON.parse(localStorage.getItem('user')) || {}
    return hasValidSession(user) ? user : {}
  } catch {
    return {}
  }
}

export default function userUpdate(state = getInitialUserState(), { type, payload }) {
  switch (type) {
    case UPDATE_USERNAME:
      return { ...state, name: payload }
    case USER_LOGGED_OUT:
      return {}
    case SET_USER: {
      return { ...state, ...payload }
    }
    case SET_USER_IMAGE:
      return { ...state, userImage: payload }
    default:
      return state
  }
}

export const authActions = {
  login,
  logout,
  updateUsername,
}
